import { create } from 'zustand';
import type { Guide, Step, PublishProgress } from '../types';
import { getGuide, deleteGuide, updateGuideSettings } from '../services/api';

interface GuideState {
  // Current guide data
  currentGuide: Guide | null;
  guideToPublish: Guide | null;
  steps: Step[];
  
  // Publishing state
  publishProgress: PublishProgress | null;
  isPublishing: boolean;
  publishError: string | null;
  
  // Guide operations state
  isSaving: boolean;
  saveError: string | null;
  
  // Actions
  setCurrentGuide: (guide: Guide) => void;
  setGuideToPublish: (guide: Guide, steps: Step[]) => void;
  getGuideToPublish: () => Guide | null;
  getCurrentGuide: () => Guide | null;
  setSteps: (steps: Step[]) => void;
  updateGuide: (updates: Partial<Guide>) => void;
  updateSteps: (steps: Step[]) => void;
  
  // Publishing actions
  startPublishing: (progress: PublishProgress) => void;
  updatePublishProgress: (progress: Partial<PublishProgress>) => void;
  finishPublishing: (success: boolean, error?: string) => void;
  clearPublishState: () => void;
  
  // Guide operations
  startSaving: () => void;
  finishSaving: (success: boolean, error?: string) => void;
  clearGuideState: () => void;
  
  // API operations
  updateGuideSettings: (guideId: string, settings: {
    publicUrl: string;
    isPublished: boolean;
    publishSettings: Guide['publishSettings'];
  }) => Promise<boolean>;
  deleteGuide: (guideId: string) => Promise<boolean>;
  getGuide: (guideId: string) => Promise<Guide | null>;
}

export const useGuideStore = create<GuideState>((set, get) => ({
  // Initial state
  currentGuide: null,
  guideToPublish: null,
  steps: [],
  publishProgress: null,
  isPublishing: false,
  publishError: null,
  isSaving: false,
  saveError: null,
  
  // Guide actions
  setCurrentGuide: (guide) => set({ currentGuide: guide }),
  
  setGuideToPublish: (guide, steps) => set({ 
    guideToPublish: guide, 
    steps 
  }),
  getGuideToPublish: () => get().guideToPublish,
  getCurrentGuide: () => get().currentGuide,

  setSteps: (steps) => set({ steps }),
  
  updateGuide: (updates) => set((state) => ({
    currentGuide: state.currentGuide ? {
      ...state.currentGuide,
      ...updates
    } : null,
    guideToPublish: state.guideToPublish ? {
      ...state.guideToPublish,
      ...updates
    } : null
  })),
  
  updateSteps: (steps) => set({ steps }),
  
  // Publishing actions
  startPublishing: (progress) => set({
    isPublishing: true,
    publishProgress: progress,
    publishError: null
  }),
  
  updatePublishProgress: (progress) => set((state) => ({
    publishProgress: state.publishProgress ? {
      ...state.publishProgress,
      ...progress
    } : null
  })),
  
  finishPublishing: (success, error) => set({
    isPublishing: false,
    publishError: error || null,
    publishProgress: success ? null : get().publishProgress
  }),
  
  clearPublishState: () => set({
    publishProgress: null,
    isPublishing: false,
    publishError: null
  }),
  
  // Guide operations
  startSaving: () => set({
    isSaving: true,
    saveError: null
  }),
  
  finishSaving: (_success, error) => set({
    isSaving: false,
    saveError: error || null
  }),
  
  clearGuideState: () => set({
    currentGuide: null,
    guideToPublish: null,
    steps: [],
    publishProgress: null,
    isPublishing: false,
    publishError: null,
    isSaving: false,
    saveError: null
  }),
  
  // API operations
  updateGuideSettings: async (guideId: string, settings: {
    publicUrl: string;
    isPublished: boolean;
    publishSettings: Guide['publishSettings'];
  }) => {
    try {
      const response = await updateGuideSettings(guideId, settings);
      
      if (response.ok && response.guide) {
        // Update local state with the returned guide data
        set((state) => ({
          currentGuide: state.currentGuide && state.currentGuide.id === guideId
            ? response.guide!
            : state.currentGuide,
          guideToPublish: state.guideToPublish && state.guideToPublish.id === guideId
            ? response.guide!
            : state.guideToPublish
        }));
        
        return true;
      } else {
        console.error('Failed to update guide settings:', response.error || response.message);
        return false;
      }
    } catch (error) {
      console.error('Error updating guide settings:', error);
      return false;
    }
  },
  
  deleteGuide: async (guideId: string) => {
    try {
      const response = await deleteGuide(guideId);
      
      if (response.ok) {
        // Clear current guide if it's the one being deleted
        set((state) => ({
          currentGuide: state.currentGuide?.id === guideId ? null : state.currentGuide,
          guideToPublish: state.guideToPublish?.id === guideId ? null : state.guideToPublish
        }));
        
        return true;
      } else {
        console.error('Failed to delete guide:', response.error || response.message);
        return false;
      }
    } catch (error) {
      console.error('Error deleting guide:', error);
      return false;
    }
  },
  
  getGuide: async (guideId: string) => {
    try {
      const response = await getGuide(guideId);
      
      if (response.ok && response.guide) {
        // Update local state with the fetched guide
        set(() => ({
          currentGuide: response.guide!
        }));
        
        return response.guide;
      } else {
        console.error('Failed to get guide:', response.error || response.message);
        return null;
      }
    } catch (error) {
      console.error('Error getting guide:', error);
      return null;
    }
  }
}));